import useAutomationValues from './useAutomationValues';

import {
  getCustomAttributeInputType,
  filterCustomAttributes,
  getStandardAttributeInputType,
  isCustomAttribute,
} from 'dashboard/helper/automationHelper';
import {
  createWorkflowStepId,
  toDateTimeWindowForm,
} from 'dashboard/routes/dashboard/settings/automation/dateTimeWindow';

export function useEditableAutomation() {
  const { getConditionDropdownValues, getActionDropdownValues } =
    useAutomationValues();

  /**
   * This function sets the conditions for automation.
   * It help to format the conditions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing conditions to manifest.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @returns {Array} An array of manifested conditions.
   */
  const manifestConditions = (
    automation,
    allCustomAttributes,
    automationTypes
  ) => {
    const customAttributes = filterCustomAttributes(allCustomAttributes);
    return automation.conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );
      let inputType = 'plain_text';
      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          automation.event_name,
          condition.attribute_key
        );
      }
      if (inputType === 'plain_text' || inputType === 'date') {
        return { ...condition, values: condition.values[0] };
      }
      if (inputType === 'comma_separated_plain_text') {
        return { ...condition, values: condition.values.join(',') };
      }
      const dropdownValues = getConditionDropdownValues(
        condition.attribute_key
      );
      const hasBooleanOptions =
        inputType === 'search_select' &&
        dropdownValues.length &&
        dropdownValues.every(item => typeof item.id === 'boolean');

      if (hasBooleanOptions) {
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: dropdownValues.find(item => item.id === condition.values[0]),
        };
      }
      return {
        ...condition,
        query_operator: condition.query_operator || 'and',
        values: [...dropdownValues].filter(item =>
          [...condition.values].includes(item.id)
        ),
      };
    });
  };

  /**
   * Generates an array of actions for the automation.
   * @param {Object} action - The action object.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array|Object} Generated actions array or object based on input type.
   */
  const generateActionsArray = (action, automationActionTypes) => {
    const params = action.action_params;
    const inputType = automationActionTypes.find(
      item => item.key === action.action_name
    ).inputType;
    if (action.action_name === 'date_time_window') {
      const payload = Array.isArray(params) ? params[0] : params;
      return toDateTimeWindowForm(payload);
    }

    if (action.action_name === 'assign_team') {
      const payload = params[0] || {};
      const teamId =
        payload.team_id || payload.team_id?.id || params[0] || null;
      const selectedTeam = [
        ...getActionDropdownValues(action.action_name),
      ].find(item => item.id === teamId);

      return {
        team_id: selectedTeam || null,
        assignment_logic: payload.assignment_logic || 'round_robin',
        only_online_users: Boolean(payload.only_online_users),
        limit_open_contacts_enabled: Boolean(
          payload.limit_open_contacts_enabled
        ),
        max_open_contacts: payload.max_open_contacts ?? 20,
        add_timeout_branch: Boolean(payload.add_timeout_branch),
        timeout_value: payload.timeout_value ?? 7,
        timeout_unit: payload.timeout_unit || 'days',
      };
    }

    if (action.action_name === 'update_contact_tag') {
      const payload = params[0] || {};
      const tags = payload.tags || [];

      return {
        operation: payload.operation || 'add',
        tags: [...getActionDropdownValues(action.action_name)].filter(item =>
          tags.includes(item.id)
        ),
      };
    }

    if (inputType === 'multi_select' || inputType === 'search_select') {
      let normalizedParams = params;

      if (action.action_name === 'assign_ai_agent') {
        normalizedParams = params.map(param => `ai_agent:${param}`);
      } else if (action.action_name === 'assign_agent') {
        normalizedParams = params.map(param =>
          ['nil', 'last_responding_agent'].includes(param)
            ? param
            : `user:${param}`
        );
      }

      return [...getActionDropdownValues(action.action_name)].filter(item =>
        [...normalizedParams].includes(item.id)
      );
    }
    if (inputType === 'team_message') {
      return {
        team_ids: [...getActionDropdownValues(action.action_name)].filter(
          item => [...params[0].team_ids].includes(item.id)
        ),
        message: params[0].message,
      };
    }
    return [...params];
  };

  /**
   * This function sets the actions for automation.
   * It help to format the actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing actions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array} An array of manifested actions.
   */
  const manifestActions = (automation, automationActionTypes) => {
    return automation.actions.map(action => ({
      ...action,
      id: action.id || createWorkflowStepId(),
      next_step_id: action.next_step_id || null,
      action_params: action.action_params.length
        ? generateActionsArray(action, automationActionTypes)
        : [],
    }));
  };

  /**
   * Formats the automation object for use when we edit the automation.
   * It help to format the conditions and actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object to format.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Object} A new object with formatted automation data, including automation conditions and actions.
   */
  const formatAutomation = (
    automation,
    allCustomAttributes,
    automationTypes,
    automationActionTypes
  ) => {
    return {
      ...automation,
      conditions: manifestConditions(
        automation,
        allCustomAttributes,
        automationTypes
      ),
      actions: manifestActions(automation, automationActionTypes),
    };
  };

  return { formatAutomation };
}
